import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Clock, AlertTriangle, Cpu, ExternalLink, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/apiClient';

// ⭐ Panel de métricas (plan de escalabilidad, Fase 4). Mide SOLO lo que la
// propia API puede ver: latencia y tasa de error de sus propias rutas, y
// memoria del proceso Node. CPU real del contenedor, cuota de Turso y
// backups viven en paneles externos (Render / Turso) — no se inventan acá,
// se listan como recordatorio (ver `externalChecks` que manda el backend).
function MetricsPanel() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rangeHours, setRangeHours] = useState(24);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      const [summaryRes, historyRes] = await Promise.all([
        api.get('/api/metrics'),
        api.get('/api/metrics/history', { params: { hours: rangeHours } })
      ]);
      setSummary(summaryRes.data);
      setHistory(
        historyRes.data.map((row) => ({
          ...row,
          label: new Date(row.created_at).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
        }))
      );
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Error cargando métricas:', e);
    } finally {
      setLoading(false);
    }
  }, [rangeHours]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000); // refresco automático cada 30s
    return () => clearInterval(interval);
  }, [load]);

  const live = summary?.live;
  const proc = summary?.process;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" /> Salud de la API
          </h3>
          <p className="text-sm text-gray-500">
            Últimos {live?.windowMinutes ?? 5} min en vivo · se refresca solo cada 30s
            {lastRefresh && ` · actualizado ${lastRefresh.toLocaleTimeString('es-EC')}`}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>

      {!summary && loading && (
        <div className="text-center py-12 text-gray-500">Cargando métricas...</div>
      )}

      {summary && (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> p95 latencia</p>
              <p className="text-2xl font-bold text-gray-900">{live?.p95Ms != null ? `${live.p95Ms} ms` : '—'}</p>
              <p className="text-xs text-gray-400 mt-1">p50 {live?.p50Ms != null ? `${live.p50Ms}ms` : '—'} · p99 {live?.p99Ms != null ? `${live.p99Ms}ms` : '—'}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Errores 5xx</p>
              <p className={`text-2xl font-bold ${live?.error5xxCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {live?.error5xxCount ?? 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">{live?.error5xxRate ?? 0}% de {live?.requestCount ?? 0} solicitudes</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Cpu className="w-3.5 h-3.5" /> Memoria (proceso)</p>
              <p className="text-2xl font-bold text-gray-900">{proc?.rssMb ?? '—'} MB</p>
              <p className="text-xs text-gray-400 mt-1">heap usado {proc?.heapUsedMb ?? '—'} MB</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 mb-1">Uptime / DB</p>
              <p className="text-2xl font-bold text-gray-900">{proc ? Math.floor(proc.uptimeSeconds / 3600) : '—'}h</p>
              <p className="text-xs text-gray-400 mt-1">{summary.db?.mode === 'turso_cloud' ? 'Turso (cloud)' : 'SQLite local'}</p>
            </div>
          </div>

          {/* ⭐ Consumo de Turso (filas leídas/escritas) — planeación de
              infraestructura, ver MARKETOS-Infraestructura-y-Costos.docx.
              Aproximación a nivel de app, no el conteo exacto que factura
              Turso — sirve para ver tendencia, no para cuadrar la factura. */}
          {summary.db && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Consumo de base de datos (Turso) · últimos {summary.db.windowMinutes} min</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Consultas</p>
                  <p className="text-xl font-bold text-gray-900">{summary.db.callCount ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Filas leídas</p>
                  <p className="text-xl font-bold text-gray-900">{summary.db.rowsRead ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Filas escritas</p>
                  <p className="text-xl font-bold text-gray-900">{summary.db.rowsWritten ?? '—'}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Estimado por la propia app, no el número exacto que factura Turso — para eso: turso.tech → tu base de datos → Usage.</p>
            </div>
          )}

          {/* Gráfico de tendencia */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-700">Tendencia de latencia p95 (snapshots cada 5 min)</p>
              <div className="flex gap-1">
                {[24, 24 * 7].map((h) => (
                  <button
                    key={h}
                    onClick={() => setRangeHours(h)}
                    className={`px-2.5 py-1 text-xs rounded-md ${rangeHours === h ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {h === 24 ? '24h' : '7 días'}
                  </button>
                ))}
              </div>
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center">
                Todavía no hay snapshots guardados (el primero se crea a los 5 min de que el servidor arranque).
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={30} />
                  <YAxis tick={{ fontSize: 11 }} unit="ms" />
                  <Tooltip
                    formatter={(value, name) => [`${value} ms`, name]}
                    labelFormatter={(label) => `Hora: ${label}`}
                  />
                  <Line type="monotone" dataKey="p95_ms" name="p95" stroke="#2563eb" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="p50_ms" name="p50" stroke="#93c5fd" dot={false} strokeWidth={1.5} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Recordatorio de chequeos externos */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-800 mb-2">
              Esto NO alcanza a ver: CPU/RAM real del contenedor, cuota de Turso ni backups. Revisa a mano:
            </p>
            <ul className="space-y-1">
              {(summary.externalChecks || []).map((c, i) => (
                <li key={i} className="text-sm text-amber-700 flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  <span className="font-medium">{c.label}:</span> {c.where}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default MetricsPanel;
